import { toast } from "@jsq/ds/core";
import { ProfileRelationshipEnum } from "../../graphql/supergraphEnums";
import {
  useCreateIndividualProfileMutation,
  useCreateInstitutionProfileMutation,
  useCreateJointProfileMutation,
  useUpdateDeclaredIndividualMutation,
  useUpdateDeclaredInstitutionMutation,
  useUpdateDeclaredJointMutation,
} from "../../graphql/supergraphHooks";
import {
  type InstitutionAccountTypeEnum,
  type IndividualAccountTypeEnum,
  type JointAccountTypeEnum,
  type ComplianceIndividualProfileInput,
  type ComplianceInstitutionProfileInput,
  type ComplianceJointProfileInput,
} from "../../graphql/supergraphTypes";
import { DeclarationFormValues } from "./ProfileDeclaration/types";

export const useCreateProfile = (
  caseId: number,
  arenaId: number,
  parentProfileId: number,
  controllerMode: boolean,
  onSuccess: () => void,
) => {
  const [createIndividualProfile, { loading: createIndividualLoading }] =
    useCreateIndividualProfileMutation({
      onError: (e) => toast.error(e.message),
      onCompleted: () => {
        toast.success("Profile created successfully");
        onSuccess();
      },
    });

  const [createInstitutionProfile, { loading: createInstitutionLoading }] =
    useCreateInstitutionProfileMutation({
      onError: (e) => toast.error(e.message),
      onCompleted: () => {
        toast.success("Profile created successfully");
        onSuccess();
      },
    });

  const [createJointProfile, { loading: createJointLoading }] = useCreateJointProfileMutation({
    onError: (e) => toast.error(e.message),
    onCompleted: () => {
      toast.success("Profile created successfully");
      onSuccess();
    },
  });

  const createIndividual = async (values: DeclarationFormValues) => {
    const hasOwnershipPercentage =
      !controllerMode && values.ownershipPercentage && values.ownershipPercentage > 0;
    const data = {
      caseId,
      firstName: values.firstName || "",
      middleName: values.middleName,
      lastName: values.lastName || "",
      addresses: [],
      accountType: values.accountType as IndividualAccountTypeEnum,
      relationshipType: [
        controllerMode ? ProfileRelationshipEnum.Controller : ProfileRelationshipEnum.Owner,
      ],
      ...(hasOwnershipPercentage && { ownershipPercentage: values.ownershipPercentage }),
    };

    await createIndividualProfile({
      variables: {
        arenaAccessId: isNaN(arenaId) ? null : arenaId,
        data,
        parentProfileId,
      },
    });
  };

  const createInstitution = async (values: DeclarationFormValues) => {
    const hasOwnershipPercentage =
      !controllerMode && values.ownershipPercentage && values.ownershipPercentage > 0;
    const data = {
      caseId,
      legalBusinessName: values.legalBusinessName || "",
      accountType: values.accountType as InstitutionAccountTypeEnum,
      relationshipType: [
        controllerMode ? ProfileRelationshipEnum.Controller : ProfileRelationshipEnum.Owner,
      ],
      addresses: [],
      ...(hasOwnershipPercentage && { ownershipPercentage: values.ownershipPercentage }),
    };

    await createInstitutionProfile({
      variables: {
        arenaAccessId: isNaN(arenaId) ? null : arenaId,
        data,
        parentProfileId,
      },
    });
  };

  const createJoint = async (values: DeclarationFormValues) => {
    const hasOwnershipPercentage =
      !controllerMode && values.ownershipPercentage && values.ownershipPercentage > 0;
    const data: ComplianceJointProfileInput = {
      caseId,
      legalBusinessName: values.legalBusinessName || "",
      accountType: values.accountType as JointAccountTypeEnum,
      relationshipType: [
        controllerMode ? ProfileRelationshipEnum.Controller : ProfileRelationshipEnum.Owner,
      ],
      ...(hasOwnershipPercentage && { ownershipPercentage: values.ownershipPercentage }),
    };

    await createJointProfile({
      variables: {
        arenaAccessId: isNaN(arenaId) ? null : arenaId,
        data,
        parentProfileId,
      },
    });
  };

  return {
    createIndividual,
    createInstitution,
    createJoint,
    isCreating: createIndividualLoading || createInstitutionLoading || createJointLoading,
  };
};

export const useUpdateProfile = (
  profileId: number,
  parentProfileId: number | null,
  onSuccess: () => void,
) => {
  const [updateDeclaredIndividual, { loading: updateIndividualLoading }] =
    useUpdateDeclaredIndividualMutation({
      onError: (e) => toast.error(e.message),
      onCompleted: () => {
        toast.success("Profile updated successfully");
        onSuccess();
      },
    });

  const [updateDeclaredInstitution, { loading: updateInstitutionLoading }] =
    useUpdateDeclaredInstitutionMutation({
      onError: (e) => toast.error(e.message),
      onCompleted: () => {
        toast.success("Profile updated successfully");
        onSuccess();
      },
    });

  const [updateDeclaredJoint, { loading: updateJointLoading }] = useUpdateDeclaredJointMutation(
    {
      onError: (e) => toast.error(e.message),
      onCompleted: () => {
        toast.success("Profile updated successfully");
        onSuccess();
      },
    },
  );

  const updateIndividual = async (payload: ComplianceIndividualProfileInput) => {
    await updateDeclaredIndividual({
      variables: {
        data: payload,
        profileId,
        parentProfileId,
      },
    });
  };

  const updateInstitution = async (payload: ComplianceInstitutionProfileInput) => {
    await updateDeclaredInstitution({
      variables: {
        data: payload,
        profileId,
        parentProfileId,
      },
    });
  };

  const updateJoint = async (payload: ComplianceJointProfileInput) => {
    await updateDeclaredJoint({
      variables: {
        data: payload,
        profileId,
        parentProfileId,
      },
    });
  };

  return {
    updateIndividual,
    updateInstitution,
    updateJoint,
    isUpdating: updateIndividualLoading || updateInstitutionLoading || updateJointLoading,
  };
};
